/**
 * This helper function constructs the URL for an optimized image from Firebase Storage,
 * assuming you have the official "Resize Images" Firebase Extension installed.
 * 
 * The extension creates resized versions of images in the same path, but with a suffix.
 * e.g., original: 'my-image.jpg', resized: 'my-image_200x200.jpg'
 * 
 * @param originalUrl The original URL of the image from Firebase Storage.
 * @param width The desired width of the resized image. Your "Resize Images" extension must be configured to create an image of this size (e.g., 400x400).
 * @returns The new URL pointing to the resized image, or the original URL if it's not a Firebase Storage URL or has issues.
 */
export const getOptimizedImageUrl = (originalUrl: string | undefined | null, width: 200 | 400 | 800 | 1200 | 1920): string | undefined => {
    // This logic assumes the "Resize Images" Firebase Extension is installed.
    // It is updated to reflect the extension's DEFAULT behavior, which is to keep the original file format (e.g., .jpg)
    // and not convert to .webp.
    if (!originalUrl || !originalUrl.includes('firebasestorage.googleapis.com')) {
        return originalUrl || undefined;
    }
    
    try {
        const url = new URL(originalUrl);
        // The pathname is already URI encoded. We decode it to safely manipulate the path.
        const decodedPathname = decodeURIComponent(url.pathname);
        
        const pathParts = decodedPathname.split('/o/');
        if (pathParts.length !== 2) {
            return originalUrl;
        }

        const pathPrefix = `${pathParts[0]}/o/`;
        const objectPath = pathParts[1];

        const lastDotIndex = objectPath.lastIndexOf('.');
        if (lastDotIndex === -1) {
            return originalUrl; // No file extension found
        }
        
        const pathWithoutExtension = objectPath.substring(0, lastDotIndex);
        const extension = objectPath.substring(lastDotIndex); // e.g., '.jpg'
        
        // The Firebase Resize Images extension creates files with a _WIDTHxHEIGHT suffix.
        // We assume square dimensions as we only pass width. For this to work, you should
        // configure the extension with square sizes (e.g., 200x200, 800x800).
        const resizedObjectPath = `${pathWithoutExtension}_${width}x${width}${extension}`;
        
        // Reconstruct the URL's pathname with the newly encoded object path.
        url.pathname = pathPrefix + encodeURIComponent(resizedObjectPath);
        
        return url.toString();
        
    } catch (error) {
        console.error("Could not parse or generate optimized image URL:", error);
        return originalUrl; // Fallback to original if parsing fails
    }
};